import React, { useRef, useState, useEffect } from 'react';
import resolveAudioUrl from '../lib/resolveAudioUrl';

const formatTime = (t) => {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioPlayer = ({ currentAudio, onClose }) => {
  const audioRef = useRef(null);
  const [resolvedUrl, setResolvedUrl] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Resolve Firebase Storage path → download URL (or pass through direct URLs)
  useEffect(() => {
    setResolvedUrl(null);
    setResolving(true);
    setError(null);
    setCurrentTime(0);
    setDuration(0);

    const source = currentAudio.audioPath || currentAudio.audioUrl;
    resolveAudioUrl(source)
      .then((url) => { setResolvedUrl(url); setResolving(false); })
      .catch(() => { setError('Could not load audio'); setResolving(false); });
  }, [currentAudio]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => setError('Playback failed'));
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const newTime = ((e.clientX - rect.left) / rect.width) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const showSpinner = resolving || loading;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{currentAudio.title}</h4>
            <p className="text-sm text-gray-500 truncate">{currentAudio.category}</p>
          </div>

          <button
            onClick={togglePlayPause}
            disabled={showSpinner || !!error}
            className="w-10 h-10 stripe-gradient rounded-full flex items-center justify-center text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex-shrink-0"
          >
            {showSpinner ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          {error ? (
            <span className="text-sm text-red-500 flex-1">{error}</span>
          ) : (
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-1 max-w-md">
              <span className="w-8 text-right">{formatTime(currentTime)}</span>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full cursor-pointer" onClick={handleSeek}>
                <div
                  className="h-full stripe-gradient rounded-full transition-all"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
              <span className="w-8">{formatTime(duration)}</span>
            </div>
          )}

          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {resolvedUrl && (
        <audio
          key={resolvedUrl}
          ref={audioRef}
          src={resolvedUrl}
          crossOrigin="anonymous"
          preload="metadata"
          autoPlay
          onPlay={() => { setIsPlaying(true); setLoading(false); }}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
          onError={() => { setError('Failed to load audio'); setLoading(false); }}
          onEnded={() => setIsPlaying(false)}
          onWaiting={() => setLoading(true)}
          onCanPlay={() => setLoading(false)}
        />
      )}
    </div>
  );
};

export default AudioPlayer;
